import { CancelContent } from '@components/display/dialogs/CancelContent';
import { Button, CloseButton } from '@components/utility/buttons';
import { APP_PREFIX_PATH } from '@constants/common';
import { FirstStep, SecondStep, SuccessStep } from '@event/routes/dashboard/EventList/EventCreation';
import { EventCreationSchema } from '@event/schemas/EventSchema';
import { useCreateEventMutation } from '@event/services/events';
import { EventLocation, IEventCreation } from '@event/types/event';
import { getEventTypeFromCreation } from '@event/utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventIcon from '@mui/icons-material/Event';
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Typography,
} from '@mui/material';
import { handleError } from '@utils/common';
import { FormikContext, useFormik } from 'formik';
import { useState } from 'react';

const EventCreationDialog = (props: DialogProps) => {
    const [showCancel, setShowCancel] = useState(false);
    const [createEvent, { isLoading, isSuccess, reset }] = useCreateEventMutation();
    const [step, setStep] = useState(0);
    const [creationId, setCreationId] = useState<number | null>(null);

    const formik = useFormik<IEventCreation>({
        initialValues: {
            name: '',
            privacy: '',
            type: '',
        },
        validationSchema: EventCreationSchema,
        onSubmit: async (values) => {
            if (values.type) {
                try {
                    const { id } = await createEvent(values).unwrap();
                    setCreationId(id);
                } catch (err) {
                    handleError('Event creation failed', err);
                }
            }
        },
    });

    const { values, resetForm, handleSubmit } = formik;

    const handleClose = () => {
        resetForm();
        setStep(0);
        if (props.onClose) {
            props.onClose({}, 'escapeKeyDown');
            setTimeout(() => {
                setShowCancel(false);
                setStep(0);
                reset();
            }, 500);
        }
    };

    const handleOpenEvent = () => {
        const eventType = getEventTypeFromCreation(values);
        window.open(`${APP_PREFIX_PATH}/dashboard/events/${eventType}/${creationId}`, '_blank');
    };

    return (
        <FormikContext value={formik}>
            <Dialog
                {...props}
                onClose={() => (isSuccess ? handleClose() : setShowCancel(true))}
                classes={{ paper: 'dialog-paper py-[40px]' }}
                slotProps={{
                    paper: {
                        component: 'form',
                        onSubmit: (e: React.SubmitEvent) => {
                            e.preventDefault();
                            handleSubmit();
                        },
                    },
                }}
            >
                {!showCancel && <CloseButton onClick={() => (isSuccess ? handleClose() : setShowCancel(true))} />}
                {!showCancel ? (
                    <>
                        {!isSuccess ? (
                            <DialogTitle className="font-roboto font-bold text-4xl text-center">
                                Create a{' '}
                                {step === 0
                                    ? 'new event'
                                    : `${values.type === EventLocation.ONSITE ? 'Onsite' : 'Virtual'} Event`}
                            </DialogTitle>
                        ) : null}
                        <DialogContent className="py-5">
                            {isSuccess ? <SuccessStep /> : step === 0 ? <FirstStep /> : <SecondStep />}
                        </DialogContent>
                        {!isSuccess && step === 0 && (
                            <DialogActions>
                                <Button
                                    fullRounded
                                    disabled={!values.type}
                                    fullWidth
                                    startIcon={<CheckBoxIcon />}
                                    onClick={() => setStep(1)}
                                >
                                    <Typography fontWeight={500} fontSize={15}>
                                        CONFIRM
                                    </Typography>
                                </Button>
                            </DialogActions>
                        )}
                        {!isSuccess && step === 1 && (
                            <DialogActions>
                                <div className="flex gap-5 w-full">
                                    <Button
                                        fullWidth
                                        fullRounded
                                        color="neutral-650"
                                        onClick={() => setStep(0)}
                                        startIcon={<ArrowBackIcon />}
                                        disabled={isLoading}
                                    >
                                        <Typography fontWeight={500} fontSize={15}>
                                            GO BACK
                                        </Typography>
                                    </Button>
                                    <Button
                                        fullWidth
                                        fullRounded
                                        startIcon={
                                            isLoading ? <CircularProgress color="inherit" size={20} /> : <EventIcon />
                                        }
                                        type="submit"
                                        disabled={isLoading}
                                    >
                                        <Typography fontWeight={500} fontSize={15}>
                                            CREATE EVENT
                                        </Typography>
                                    </Button>
                                </div>
                            </DialogActions>
                        )}
                        {isSuccess && (
                            <DialogActions>
                                <div className="flex gap-5 mt-auto w-full">
                                    <Button
                                        fullWidth
                                        fullRounded
                                        color="neutral-650"
                                        onClick={handleClose}
                                        startIcon={<DateRangeIcon />}
                                    >
                                        <Typography fontWeight={500} fontSize={15}>
                                            GO TO EVENT LIST
                                        </Typography>
                                    </Button>
                                    <Button
                                        fullWidth
                                        fullRounded
                                        startIcon={<CalendarTodayIcon />}
                                        onClick={handleOpenEvent}
                                    >
                                        <Typography fontWeight={500} fontSize={15}>
                                            VIEW EVENT
                                        </Typography>
                                    </Button>
                                </div>
                            </DialogActions>
                        )}
                    </>
                ) : (
                    <CancelContent
                        title="Are you sure you want to cancel?"
                        subtitle="By clicking discard event all unsaved changes will be lost."
                        primary={{
                            label: 'Discard event',
                            onClick: () => handleClose(),
                        }}
                        secondary={{
                            label: 'Go back',
                            onClick: () => setShowCancel(false),
                        }}
                        className="my-auto !px-0 !pt-0 !pb-0"
                    />
                )}
            </Dialog>
        </FormikContext>
    );
};

export { EventCreationDialog };
